import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiPost } from "../lib/api";

/**
 * Landing page for the emailed reset link (`/reset-password?token=…`).
 *
 * The token stays in component state and is sent in the POST body — never put in
 * a query string we generate, never logged. It arrives in the URL because email
 * links have no other channel; the trade-off is bounded by the token being
 * single-use and one hour old.
 *
 * Failure messages come from the server verbatim. It returns one identical
 * message for expired / already-used / unknown tokens on purpose, and the UI must
 * not try to be more helpful than that.
 */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError("The two password fields don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await apiPost("/api/auth/reset-password", { token, newPassword: next });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold text-slate-900">Savvy Reputation</h1>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          {!token ? (
            <>
              <h2 className="text-base font-semibold text-slate-900">
                This link is incomplete
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                The address is missing its reset code — some email clients cut long
                links. Request a new one and open it directly from the email.
              </p>
              <Link
                to="/forgot-password"
                className="mt-5 block w-full rounded-lg bg-brand-600 px-3 py-2 text-center text-sm font-medium text-white hover:bg-brand-700"
              >
                Request a new link
              </Link>
            </>
          ) : done ? (
            <>
              <h2 className="text-base font-semibold text-slate-900">
                Password updated
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                Sign in with your new password. Anyone who was signed in to this
                account has been signed out.
              </p>
              <Link
                to="/login"
                className="mt-5 block w-full rounded-lg bg-brand-600 px-3 py-2 text-center text-sm font-medium text-white hover:bg-brand-700"
              >
                Go to sign in
              </Link>
            </>
          ) : (
            <>
              <h2 className="text-base font-semibold text-slate-900">
                Choose a new password
              </h2>
              <p className="mt-1 text-sm text-slate-500">At least 12 characters.</p>
              <form onSubmit={onSubmit} className="mt-5 space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">
                    New password
                  </span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    autoFocus
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-inner focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">
                    New password again
                  </span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-inner focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200"
                  />
                </label>
                {error && (
                  <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                    <div className="mt-1">
                      <Link to="/forgot-password" className="underline">
                        Request a new link
                      </Link>
                    </div>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-60"
                >
                  {submitting ? "Saving…" : "Set new password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
